"use client";

import { useQueryState } from "nuqs";
import React, { useEffect } from "react";
import {
  Spinner,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
  Pagination,
  Link,
  Image,
} from "@heroui/react";
import { IconPepper, IconEyeOff } from "@tabler/icons-react";
import { IDecklistV2 } from "@/models/decklist";
import type { DeckIdentity } from "@/interfaces/DeckIdentity";
import { getDeckIdentity } from "@/lib/archetypeManager";
import { getPlacement } from "@/lib/placementHelper";
import { myDayjs } from "@/lib/dayjsHelper";
import { LeaderSquareWithBorder } from "@/components/leaders/LeaderSquareWithBorder";
import { SupportedGames } from "@/types/game";
import { getGameHandler } from "@/games";
import EventTypeChip from "@/components/EventTypeChip";
import { usePathname } from "next/navigation";
import { CDN } from "@/lib/cdn";
import { getRoutePrefix } from "@/games";

// import {EditIcon} from "./EditIcon";
// import {DeleteIcon} from "./DeleteIcon";
// import {EyeIcon} from "./EyeIcon";
// import {columns, users} from "./data";

function formatCardName(
  name: string | undefined,
  gameName: SupportedGames,
): string | undefined {
  if (!name) return undefined;
  // For Riftbound, only show the part before the comma
  if (gameName === "riftbound") {
    return name.split(",")[0].trim();
  }
  return name;
}

const statusColorMap = {
  active: "success",
  paused: "danger",
  vacation: "warning",
};

function formatForUrl(str: string) {
  return str.toLowerCase();
}

// Comprehensive mapping of country names and codes to 2-letter ISO codes
const countryCodeMap: { [key: string]: string } = {
  // 3-letter codes
  USA: "US",
  GBR: "GB",
  CAN: "CA",
  AUS: "AU",
  JPN: "JP",

  // Full country names
  JAPAN: "JP",
  CANADA: "CA",
  BRAZIL: "BR",
  SINGAPORE: "SG",
  "SOUTH AFRICA": "ZA",
  AUSTRALIA: "AU",
  GERMANY: "DE",
  PHILIPPINES: "PH",
  MEXICO: "MX",
  GREECE: "GR",
  ITALY: "IT",

  // Regional codes (no flags for these)
  NA: "", // North America
  EU: "", // Europe
  OC: "", // Oceania
  LA: "", // Latin America
};

// Map of 2-letter codes to full country names
const countryNameMap: { [key: string]: string } = {
  US: "United States",
  GB: "United Kingdom",
  CA: "Canada",
  AU: "Australia",
  JP: "Japan",
  BR: "Brazil",
  SG: "Singapore",
  ZA: "South Africa",
  DE: "Germany",
  PH: "Philippines",
  MX: "Mexico",
  GR: "Greece",
  IT: "Italy",
};

// Get normalized 2-letter country code
function getNormalizedCountryCode(countryCode: string | undefined): string {
  if (!countryCode) return "";

  let code = countryCode.toUpperCase().trim();

  // Check if it's in our mapping
  if (countryCodeMap.hasOwnProperty(code)) {
    code = countryCodeMap[code];
    if (!code) return ""; // Regional codes return empty
  }

  // Only return 2-letter codes
  if (code.length !== 2) return "";

  return code;
}

// Convert country code to flag emoji (e.g., "US" -> "🇺🇸", "Japan" -> "🇯🇵")
function getCountryFlag(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return "";

  const codePoints = countryCode
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));

  return String.fromCodePoint(...codePoints);
}

// Get full country name from 2-letter code
function getCountryName(code: string): string {
  return countryNameMap[code] || code;
}

export default function DecklistsTable({
  game,
  decklists,
  isLoading,
  showPagination = true,
  palworldArchetypes,
}: {
  game: SupportedGames;
  decklists: IDecklistV2[];
  isLoading: boolean;
  showPagination?: boolean;
  palworldArchetypes?: Record<string, any>;
}) {
  const pathname = usePathname();
  const [page, setPage] = React.useState(1);
  const rowsPerPage = 50;

  const pages = Math.max(Math.ceil(decklists.length / rowsPerPage), 1);

  const items = React.useMemo(() => {
    if (!showPagination) {
      return decklists;
    }
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;

    return decklists.slice(start, end);
  }, [page, decklists, showPagination]);

  useEffect(() => {
    setPage(1);
  }, [decklists.length]);

  const renderCell = React.useCallback(
    (decklist: IDecklistV2, columnKey: string) => {
      const cellValue = decklist[columnKey as keyof typeof decklist];
      const gamePath = getRoutePrefix(game);

      switch (columnKey) {
        case "leader":
          const leaderId = decklist.leader_id;
          const gameHandler = getGameHandler(game);

          // For Palworld, check if archetype data was pre-fetched from server
          let leaderData: DeckIdentity | undefined;
          if (game === "palworld" && leaderId && palworldArchetypes) {
            leaderData = palworldArchetypes[leaderId];
          }

          // Fallback to standard getDeckIdentity (which will derive from decklist if needed)
          if (!leaderData) {
            leaderData = getDeckIdentity(game, leaderId, decklist.decklist);
          }

          const hasDecklist =
            decklist.decklist != null &&
            decklist.decklist.trim() !== "" &&
            decklist.decklist !== "[]" &&
            decklist.decklist !== "{}";

          const leaderImage = (
            <div className="relative">
              <div style={{ filter: !hasDecklist ? "grayscale(20%)" : "none" }}>
                <LeaderSquareWithBorder
                  leaderId={leaderId}
                  colors={leaderData?.color}
                  image={leaderData?.image}
                  size={44}
                  borderSize={game === "union-arena" ? 2 : 1}
                  gameName={game}
                />
              </div>
              {!hasDecklist && (
                <div className="absolute bottom-0 right-0 z-10">
                  <IconEyeOff
                    size={18}
                    className="text-slate-200 drop-shadow-lg"
                  />
                </div>
              )}
            </div>
          );

          const content = (
            <div className="flex flex-row items-center w-[220px] gap-3">
              {!hasDecklist ? (
                <Tooltip content="No Decklist">{leaderImage}</Tooltip>
              ) : (
                leaderImage
              )}
              <div className="flex-1">
                <div className="flex flex-row">
                  {getSpicy(decklist.spice)}
                  <div
                    className={`line-clamp-1 text-sm ${!hasDecklist ? "text-slate-500" : "text-slate-200"}`}
                  >
                    {formatCardName(
                      leaderData?.name || decklist.leader_name,
                      game,
                    )}
                  </div>
                </div>
                <div className="text-slate-500 text-tiny">
                  {game === "riftbound"
                    ? leaderData?.color
                    : [leaderData?.color, leaderData?.set]
                        .filter(Boolean)
                        .join(" ")}
                </div>
              </div>
              {/* <User
                avatarProps={{
                  radius: "sm",
                  src: getLeaderSquare(leaderId),
                  className: "min-w-[40px] rounded-outline",
                }}
                description={<div>{leaderData.color}</div>}
                name={
                  <div className="flex flex-row">
                    {getSpicy(decklist.spice as string)}
                    <div className="line-clamp-1">{leaderData.name}</div>
                  </div>
                }
                className="text-slate-200"
              ></User> */}
            </div>
          );

          return hasDecklist ? (
            <Link
              href={`/${gamePath}decklists/deck/${formatForUrl(decklist.region)}/${formatForUrl(decklist.set)}/${decklist.id}`}
            >
              {content}
            </Link>
          ) : (
            content
          );
        case "source":
          return (
            <div className="w-[40px] line-clamp-2 flex justify-center">
              {getLogo(decklist.source_provider)}
            </div>
          );
        case "author":
          const countryCode = getNormalizedCountryCode(decklist.country);
          const flag = countryCode ? getCountryFlag(countryCode) : "";
          const countryName = countryCode ? getCountryName(countryCode) : "";

          return (
            <div className="w-[100px] line-clamp-1 flex items-center gap-1.5">
              {countryCode && (
                <Tooltip
                  content={
                    <div className="flex items-center gap-1.5">
                      {flag && <span>{flag}</span>}
                      <span>{countryName}</span>
                    </div>
                  }
                >
                  <div className="px-1 py-0.5 text-[10px] font-semibold border border-slate-600 rounded bg-slate-800/50 text-slate-400 leading-none">
                    {countryCode}
                  </div>
                </Tooltip>
              )}
              <span className="truncate">{cellValue}</span>
            </div>
          );
        case "event-type":
          return (
            <div className="text-center w-[100px]">
              <EventTypeChip eventType={decklist.tournament_type} />
            </div>
          );
        case "place":
          return (
            <div className="text-center w-[44px]">
              {getPlacement(decklist.placement, decklist.placement_text)}
            </div>
          );
        case "players":
          return (
            <div className="text-center w-[40px]">{decklist.participants}</div>
          );
        case "location":
          const eventUrl = `/${gamePath}events/${decklist.event_id}`;
          const isCurrentPage = pathname === eventUrl;

          return (
            <div className="text-right w-[180px]">
              {decklist.event_id && !isCurrentPage ? (
                <a
                  href={eventUrl}
                  className="flex items-start justify-end gap-1 text-sm text-slate-400 hover:text-slate-200 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="flex-shrink-0 mt-0.5"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <span className="line-clamp-2">{decklist.event_name}</span>
                </a>
              ) : (
                <div className="flex items-start justify-end gap-1 text-sm">
                  {decklist.event_id && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="flex-shrink-0 mt-0.5"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  )}
                  <span className="line-clamp-2">{decklist.event_name}</span>
                </div>
              )}
            </div>
          );
        case "date":
          // check if today
          if (
            myDayjs().format("M/D") === myDayjs(decklist.date).format("M/D")
          ) {
            // if (decklist.timestamp) {
            //   return (
            //     <div className="text-center text-xs">
            //       {myDayjs(parseInt(decklist.timestamp) * 1000).fromNow()}
            //     </div>
            //   );
            // } else {
            return (
              <div className="text-center text-slate-300 w-[30px]">
                {myDayjs(decklist.date).format("M/D")}
              </div>
            );
            // }
          } else {
            return (
              <div className="text-center">
                {myDayjs(decklist.date).format("M/D")}
              </div>
            );
          }

        // case "role":
        //   return (
        //     <div className="flex flex-col">
        //       <p className="text-bold text-sm capitalize">{cellValue}</p>
        //       <p className="text-bold text-sm capitalize text-default-400">{cellValue}</p>
        //     </div>
        //   );
        // case "status":
        //   return (
        //     <Chip className="capitalize" size="sm" variant="flat">
        //       {cellValue}
        //     </Chip>
        //   );
        // case "actions":
        //   return (
        //     <div className="relative flex items-center gap-2">
        //       <Tooltip content="Details">
        //         <span className="text-lg text-default-400 cursor-pointer active:opacity-50">
        //         </span>
        //       </Tooltip>
        //       <Tooltip content="Edit user">
        //         <span className="text-lg text-default-400 cursor-pointer active:opacity-50">
        //         </span>
        //       </Tooltip>
        //       <Tooltip color="danger" content="Delete user">
        //         <span className="text-lg text-danger cursor-pointer active:opacity-50">
        //         </span>
        //       </Tooltip>
        //     </div>
        //   );
        default:
          return cellValue;
      }
    },
    [game, pathname],
  );

  const columns = [
    { name: "date", display: "Date", uid: "date" },
    { name: "leader", display: "Leader", uid: "leader" },
    {
      name: "eventType",
      display: "Event Type",
      uid: "event-type",
      className: "text-center",
    },
    { name: "author", display: "Author", uid: "author" },
    { name: "place", display: "Place", uid: "place", className: "text-center" },
    {
      name: "players",
      display: "Players",
      uid: "players",
      className: "text-center",
    },
    {
      name: "source",
      display: "Source",
      uid: "source",
      className: "text-center",
    },
    {
      name: "location",
      display: "Location/Event",
      uid: "location",
      className: "text-right",
    },
  ];

  return (
    <Table
      // selectionMode="single"
      bottomContent={
        showPagination ? (
          <div className="flex w-full justify-center">
            <Pagination
              isCompact
              showControls
              showShadow
              color="primary"
              page={page}
              total={pages}
              onChange={(page) => setPage(page)}
            />
          </div>
        ) : undefined
      }
      aria-label="Example table with custom cells"
      classNames={{
        table: "min-h-[200px]",
      }}
    >
      <TableHeader columns={columns}>
        {(column) => (
          <TableColumn key={column.uid} className={column.className}>
            {column.display}
          </TableColumn>
        )}
      </TableHeader>
      <TableBody
        isLoading={isLoading}
        loadingContent={<Spinner label="Loading..." />}
        emptyContent={"No decks to display. Coming soon!"}
        items={items}
      >
        {(item) => (
          <TableRow key={item.id} className="text-slate-400 h-[56px]">
            {(columnKey) => (
              <TableCell key={Math.random()}>
                {renderCell(item, columnKey as string)}
              </TableCell>
            )}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function getSpicy(spice: number | null) {
  if (!spice) {
    return <></>;
  }
  const spiceNum = spice;

  if (spiceNum >= 90) {
    return (
      <Tooltip content="Spicy Decklist">
        <IconPepper
          color="red"
          height={18}
          width={18}
          className="min-w-[18px]"
        />
      </Tooltip>
    );
  } else {
    return <></>;
  }
}

function getLogo(source: string) {
  switch (source) {
    case "egman":
      return (
        <Tooltip content="egmanevents">
          <Image
            src={`${CDN}/shared/logo/other/egman_logo.png`}
            alt="egmanevents"
            width={20}
            style={{ filter: "grayscale(10%)" }}
          />
        </Tooltip>
      );
    case "limitless":
      return (
        <Tooltip content="limitless">
          <Image
            src={`${CDN}/shared/logo/other/limitless_logo.png`}
            alt="limitless"
            width={40}
            style={{ filter: "grayscale(10%)" }}
          />
        </Tooltip>
      );
    default:
      return <></>;
  }
}
